import { useMemo, useState } from "react";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ArrowDownRight, ArrowRight, ArrowUpRight, Settings2 } from "lucide-react";
import { AppHeader } from "@/components/app/AppHeader";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth-context";
import { ManageGoalsDialog } from "@/components/creative-production/ManageGoalsDialog";
import { CustomRangePicker } from "@/components/creative-production/CustomRangePicker";
import {
  useCreativeDirectory,
  useCreativeMembers,
  useCreativeReport,
  useDepartmentGoals,
} from "@/hooks/use-creative-production";
import {
  allowedCreativeDepts,
  canManageGoals,
  contributorCount,
  currentMonth,
  currentWeek,
  daysOf,
  inRange,
  isProductionEmployee,
  perUser,
  pctChange,
  periodOf,
  previousWeek,
  RANGE_LABELS,
  resolveGoal,
  selectionFromPreset,
  totalFor,
  CREATIVE_DEPT_LABELS,
  type CreativeDept,
  type DepartmentGoal,
  type ProductionMember,
  type RangeKey,
  type ReportRow,
  type Selection,
} from "@/lib/creative-production";
import { formatDateOnly } from "@/lib/tz";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/creative-production")({
  component: CreativeProductionPage,
});

const RANGE_NOUN: Record<RangeKey, string> = {
  today: "Today",
  week: "This Week",
  month: "This Month",
};

/** "25 Aug – 31 Aug" / "28 Dec 2026 – 03 Jan 2027" */
function rangeLabel(from: string, to: string): string {
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  return sameYear
    ? `${formatDateOnly(from, "dd MMM")} – ${formatDateOnly(to, "dd MMM")}`
    : `${formatDateOnly(from, "dd MMM yyyy")} – ${formatDateOnly(to, "dd MMM yyyy")}`;
}


function CreativeProductionPage() {
  const { role, profile, loading } = useAuth();
  const depts = useMemo(() => allowedCreativeDepts(role, profile), [role, profile]);
  const [selection, setSelection] = useState<Selection>(() => selectionFromPreset("week"));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [goalsOpen, setGoalsOpen] = useState(false);

  const enabled = depts.length > 0;
  const sel = periodOf(selection);
  const report = useCreativeReport(sel, enabled);
  const directory = useCreativeDirectory(depts, enabled);
  const membersQ = useCreativeMembers(enabled);
  const goalsQ = useDepartmentGoals(enabled);

  if (loading) return null;
  if (!enabled) return <Navigate to="/dashboard" />;

  const rows = (report.data ?? []) as ReportRow[];
  const goals = (goalsQ.data ?? []) as DepartmentGoal[];
  const members = (membersQ.data ?? []) as ProductionMember[];
  const week = currentWeek();
  const prev = previousWeek();
  const month = currentMonth();
  const canEditGoals = canManageGoals(role, profile);
  const isCustom = selection.key === "custom";
  const selLabel = rangeLabel(sel.from, sel.to);

  const eligibleIds = (dept: CreativeDept) =>
    new Set(members.filter((m) => isProductionEmployee(m, dept)).map((m) => m.id));

  const exportCsv = () => {
    const header = ["Department", "Employee", "Date", "Output"];
    const nameOf = (id: string) =>
      directory.data?.find((p) => p.id === id)?.full_name ?? id;
    const lines = [header.join(",")];
    for (const r of rows) {
      if (!depts.includes(r.department) || !inRange(r.submission_date, sel)) continue;
      lines.push(
        [
          CREATIVE_DEPT_LABELS[r.department],
          `"${nameOf(r.user_id).replace(/"/g, '""')}"`,
          r.submission_date,
          r.output_count,
        ].join(","),
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `creative-production_${sel.from}_to_${sel.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="pb-8">
      <AppHeader
        subtitle="Creative Production · Video Editing & Script Writing performance"
        onExport={exportCsv}
        extraActions={
          <>
            <Select
              value={selection.key}
              onValueChange={(v) => {
                if (v === "custom") {
                  setPickerOpen(true);
                  return;
                }
                setSelection(selectionFromPreset(v as RangeKey));
              }}
            >
              <SelectTrigger className="h-9 w-[170px] text-[11px]">
                <SelectValue>{isCustom ? selLabel : RANGE_LABELS[selection.key as RangeKey]}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(RANGE_LABELS) as RangeKey[]).map((k) => (
                  <SelectItem key={k} value={k} className="text-[12px]">
                    {RANGE_LABELS[k]}
                  </SelectItem>
                ))}
                <SelectItem value="custom" className="text-[12px]">
                  Custom Range
                </SelectItem>
              </SelectContent>
            </Select>
            {canEditGoals && (
              <Button
                variant="outline"
                size="sm"
                className="h-9 text-[11px]"
                onClick={() => setGoalsOpen(true)}
              >
                <Settings2 className="h-3.5 w-3.5" />
                Manage Goals
              </Button>
            )}
          </>
        }
      />

      <CustomRangePicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        from={isCustom ? sel.from : undefined}
        to={isCustom ? sel.to : undefined}
        onApply={(from, to) => setSelection({ key: "custom", from, to })}
      />

      <div className="px-4 pt-5 sm:px-[22px]">
        <h1 className="text-[20px] font-bold leading-tight">Creative Production</h1>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Video Editing &amp; Script Writing performance ·{" "}
          {formatDateOnly(sel.from, "dd MMM")} – {formatDateOnly(sel.to, "dd MMM yyyy")}
        </p>
      </div>


      {/* KPI ROW — exactly two cards */}
      <div className="grid grid-cols-1 gap-3 px-4 py-4 sm:px-[22px] md:grid-cols-2">
        {depts.includes("editor") && (
          <KpiCard
            label={`Videos ${isCustom ? selLabel : RANGE_NOUN[selection.key as RangeKey]}`}
            value={totalFor(rows, "editor", sel)}
            sub={`across ${contributorCount(rows, "editor", sel, eligibleIds("editor"))} editor${
              contributorCount(rows, "editor", sel, eligibleIds("editor")) === 1 ? "" : "s"
            }`}
            accent
          />
        )}
        {depts.includes("scriptwriter") && (
          <KpiCard
            label={`Scripts ${isCustom ? selLabel : RANGE_NOUN[selection.key as RangeKey]}`}
            value={totalFor(rows, "scriptwriter", sel)}
            sub={`across ${contributorCount(rows, "scriptwriter", sel, eligibleIds("scriptwriter"))} writer${
              contributorCount(rows, "scriptwriter", sel, eligibleIds("scriptwriter")) === 1 ? "" : "s"
            }`}
          />
        )}
      </div>

      {/* LEADERBOARDS */}
      <div className="grid grid-cols-1 gap-3 px-4 sm:px-[22px] lg:grid-cols-2">
        {depts.includes("editor") && (
          <Leaderboard
            title="Editor Leaderboard"
            dept="editor"
            rows={rows}
            period={sel}
            people={(directory.data ?? []).filter((p) => eligibleIds("editor").has(p.id))}
            loading={report.isLoading || directory.isLoading || membersQ.isLoading}
          />
        )}
        {depts.includes("scriptwriter") && (
          <Leaderboard
            title="Scriptwriters"
            dept="scriptwriter"
            rows={rows}
            period={sel}
            people={(directory.data ?? []).filter((p) => eligibleIds("scriptwriter").has(p.id))}
            loading={report.isLoading || directory.isLoading || membersQ.isLoading}
          />
        )}
      </div>


      {/* GOALS */}
      <div className="grid grid-cols-1 gap-3 px-4 pt-3 sm:px-[22px] lg:grid-cols-2">
        <GoalPanel
          title="Weekly Goal Progress"
          caption={`${formatDateOnly(week.from, "dd MMM")} – ${formatDateOnly(week.to, "dd MMM")}`}
          depts={depts}
          rows={rows}
          goals={goals}
          periodType="weekly"
          period={week}
        />
        <GoalPanel
          title="Monthly Goal Progress"
          caption={formatDateOnly(month.from, "MMMM yyyy")}
          depts={depts}
          rows={rows}
          goals={goals}
          periodType="monthly"
          period={month}
        />
      </div>

      {/* OUTPUT VS TARGET */}
      <div className="px-4 pt-3 sm:px-[22px]">
        <OutputVsTarget
          depts={depts}
          rows={rows}
          goals={goals}
          range={selection.key === "month" ? "month" : "week"}
          custom={isCustom ? sel : undefined}

        />
      </div>

      {/* FOOTER STRIP */}
      <div className="px-4 pt-3 sm:px-[22px]">
        <WeekComparison depts={depts} rows={rows} week={week} prev={prev} />
      </div>

      {canEditGoals && profile && (
        <ManageGoalsDialog
          open={goalsOpen}
          onOpenChange={setGoalsOpen}
          goals={goals}
          depts={depts}
          userId={profile.id}
        />
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: number;
  sub: string;
  accent?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="text-[9px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-1 text-[30px] font-extrabold leading-tight",
          accent && "text-[#7C3AED]",
        )}
      >
        {value}
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>
    </div>
  );
}

function Bar({ pct, muted }: { pct: number; muted?: boolean }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <div
        className={cn("h-full rounded-full", muted ? "bg-muted-foreground/40" : "bg-[#7C3AED]")}
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
      />
    </div>
  );
}

function Leaderboard({
  title,
  dept,
  rows,
  period,
  people,
  loading,
}: {
  title: string;
  dept: CreativeDept;
  rows: ReportRow[];
  period: { from: string; to: string };
  people: { id: string; full_name: string; avatar_url: string | null; department: string; status: string }[];
  loading: boolean;
}) {
  const counts = perUser(rows, dept, period);
  const members = people.filter((p) => p.department === dept);
  const byId = new Map(members.map((m) => [m.id, m]));
  // Only eligible production employees (passed in via `people`) can appear —
  // team leads / admins / moderators are excluded even if they have output.
  const ids = new Set<string>([
    ...[...counts.keys()].filter((id) => byId.has(id)),
    ...members.filter((m) => m.status === "active").map((m) => m.id),
  ]);

  const list = [...ids]
    .map((id) => ({
      id,
      name: byId.get(id)?.full_name ?? "Unknown",
      avatar: byId.get(id)?.avatar_url ?? null,
      count: counts.get(id) ?? 0,
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  const top = list.length ? list[0].count : 0;

  return (
    <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="mb-3 flex items-end justify-between">
        <h2 className="text-[15px] font-bold">{title}</h2>
        <span className="text-[11px] text-muted-foreground">
          {list.reduce((s, r) => s + r.count, 0)} total
        </span>
      </div>
      {loading ? (
        <div className="py-6 text-center text-[11px] text-muted-foreground">Loading…</div>
      ) : list.length === 0 ? (
        <div className="py-6 text-center text-[11px] text-muted-foreground">
          No team members found.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {list.map((p, i) => (
            <div key={p.id} className="flex items-center gap-3">
              <span className="w-4 shrink-0 text-[11px] font-bold text-muted-foreground">
                {i + 1}
              </span>
              <UserAvatar name={p.name} avatarUrl={p.avatar} className="h-8 w-8" textClassName="text-[11px]" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-[12px] font-semibold">{p.name}</span>
                  <span className="text-[12px] font-bold">{p.count}</span>
                </div>
                <div className="mt-1.5">
                  <Bar pct={top > 0 ? (p.count / top) * 100 : 0} muted={p.count === 0} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GoalPanel({
  title,
  caption,
  depts,
  rows,
  goals,
  periodType,
  period,
}: {
  title: string;
  caption: string;
  depts: CreativeDept[];
  rows: ReportRow[];
  goals: DepartmentGoal[];
  periodType: "weekly" | "monthly";
  period: { from: string; to: string };
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="mb-3 flex items-end justify-between">
        <h2 className="text-[15px] font-bold">{title}</h2>
        <span className="text-[11px] text-muted-foreground">{caption}</span>
      </div>
      <div className="flex flex-col gap-4">
        {depts.map((d) => {
          const actual = totalFor(rows, d, period);
          const target = resolveGoal(goals, d, periodType, period.from);
          const pct = target && target > 0 ? Math.round((actual / target) * 100) : null;
          const achieved = target !== null && actual >= target;
          return (
            <div key={d}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12px] font-semibold">{CREATIVE_DEPT_LABELS[d]}</span>
                {target === null ? (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                    Target not set
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <span className="text-[12px] font-bold">
                      {actual} / {target}
                    </span>
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                        achieved
                          ? "bg-[color:var(--success-bg,#DCFCE7)] text-[color:var(--success-text,#166534)]"
                          : "bg-[color:var(--danger-bg,#FEE2E2)] text-[color:var(--danger-text,#991B1B)]",
                      )}
                    >
                      {achieved ? "Achieved" : "Behind"}
                    </span>
                  </span>
                )}
              </div>
              <div className="mt-2">
                <Bar pct={pct ?? 0} muted={target === null} />
              </div>
              <div className="mt-1 text-[10px] text-muted-foreground">
                {target === null ? "No target configured" : `${pct}% achieved`}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OutputVsTarget({
  depts,
  rows,
  goals,
  range,
  custom,
}: {
  depts: CreativeDept[];
  rows: ReportRow[];
  goals: DepartmentGoal[];
  range: "week" | "month";
  custom?: { from: string; to: string };
}) {
  const isCustom = !!custom;
  const period = custom ?? (range === "week" ? currentWeek() : currentMonth());
  const periodType = range === "week" ? "weekly" : "monthly";
  const days = daysOf(period);
  const perDay = (d: CreativeDept, day: string) =>
    rows.reduce(
      (s, r) => (r.department === d && r.submission_date === day ? s + r.output_count : s),
      0,
    );

  const editorTarget = isCustom ? null : resolveGoal(goals, "editor", periodType, period.from);
  const writerTarget = isCustom
    ? null
    : resolveGoal(goals, "scriptwriter", periodType, period.from);

  let cumE = 0;
  let cumS = 0;
  const data = days.map((day, i) => {
    cumE += perDay("editor", day);
    cumS += perDay("scriptwriter", day);
    const share = (i + 1) / days.length;
    return {
      day: formatDateOnly(day, isCustom ? "dd MMM" : range === "week" ? "EEE" : "dd"),
      editing: cumE,
      scripting: cumS,
      editingTarget: editorTarget === null ? null : Math.round(editorTarget * share),
      scriptingTarget: writerTarget === null ? null : Math.round(writerTarget * share),
    };
  });

  return (
    <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="mb-1 flex items-end justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
            Cumulative
          </div>
          <h2 className="text-[15px] font-bold">
            {isCustom ? "Output Trend" : "Output vs Target"}
          </h2>
        </div>
        <span className="text-[11px] text-muted-foreground">
          {isCustom
            ? rangeLabel(period.from, period.to)
            : range === "week"
              ? "This week"
              : "This month"}
        </span>
      </div>

      <div className="mt-4 h-[240px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="cpEdit" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#7C3AED" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="cpScript" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#0EA5E9" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="day"
              tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
              tickLine={false}
              axisLine={false}
              interval={isCustom ? "preserveStartEnd" : range === "week" ? 0 : 3}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{
                fontSize: 11,
                borderRadius: 8,
                border: "1px solid var(--border)",
                background: "var(--card)",
                color: "var(--foreground)",
              }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {depts.includes("editor") && (
              <Area
                type="monotone"
                dataKey="editing"
                name="Video Editing"
                stroke="#7C3AED"
                strokeWidth={2}
                fill="url(#cpEdit)"
              />
            )}
            {depts.includes("scriptwriter") && (
              <Area
                type="monotone"
                dataKey="scripting"
                name="Script Writing"
                stroke="#0EA5E9"
                strokeWidth={2}
                fill="url(#cpScript)"
              />
            )}
            {depts.includes("editor") && editorTarget !== null && (
              <Line
                type="monotone"
                dataKey="editingTarget"
                name="Editing target"
                stroke="#7C3AED"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                dot={false}
              />
            )}
            {depts.includes("scriptwriter") && writerTarget !== null && (
              <Line
                type="monotone"
                dataKey="scriptingTarget"
                name="Script target"
                stroke="#0EA5E9"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                dot={false}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function WeekComparison({
  depts,
  rows,
  week,
  prev,
}: {
  depts: CreativeDept[];
  rows: ReportRow[];
  week: { from: string; to: string };
  prev: { from: string; to: string };
}) {
  const sum = (p: { from: string; to: string }, list: CreativeDept[]) =>
    list.reduce((s, d) => s + totalFor(rows, d, p), 0);

  const metrics: { label: string; current: number; previous: number }[] = [
    { label: "Total Output", current: sum(week, depts), previous: sum(prev, depts) },
  ];
  if (depts.includes("editor"))
    metrics.push({
      label: "Videos",
      current: totalFor(rows, "editor", week),
      previous: totalFor(rows, "editor", prev),
    });
  if (depts.includes("scriptwriter"))
    metrics.push({
      label: "Scripts",
      current: totalFor(rows, "scriptwriter", week),
      previous: totalFor(rows, "scriptwriter", prev),
    });

  return (
    <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
      <h2 className="text-[15px] font-bold">This Week vs Last Week</h2>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {metrics.map((m) => {
          const c = pctChange(m.current, m.previous);
          const Icon = c.dir === 1 ? ArrowUpRight : c.dir === -1 ? ArrowDownRight : ArrowRight;
          return (
            <div key={m.label} className="flex items-center gap-3">
              <span
                className={cn(
                  "flex h-9 w-9 items-center justify-center rounded-full",
                  c.dir === 1
                    ? "bg-[color:var(--success-bg,#DCFCE7)] text-[color:var(--success-text,#166534)]"
                    : c.dir === -1
                      ? "bg-[color:var(--danger-bg,#FEE2E2)] text-[color:var(--danger-text,#991B1B)]"
                      : "bg-muted text-muted-foreground",
                )}
              >
                <Icon className="h-4 w-4" />
              </span>
              <div>
                <div className="text-[18px] font-extrabold leading-tight">{c.label}</div>
                <div className="text-[11px] text-muted-foreground">
                  {m.label} · {m.current} vs {m.previous}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
